import { useEffect, useState } from 'react';

/**
 * Defer work until an element is near the viewport, without taking it out of
 * layout flow.
 *
 * Shared by the landing page's listing-backed sections (SectionListings,
 * SectionMaterialWall) so a below-the-fold section does not fire its query on
 * page load. Returns false until the element is within `rootMargin` of the
 * viewport, then true forever after — callers use it as a one-way "go" flag.
 *
 * WHY NOT lazyLoadWithDimensions, which upstream uses for this:
 * that HOC's wrapper is `position: absolute; inset: 0`, so the content cannot
 * size its own container and the container needs an exact pixel height computed
 * up front. That estimate can never be right for every rail — card height varies
 * with the colourway strip and with titles that wrap to two lines — and when it
 * is short the cards overflow and eat the spacing below the section. An
 * IntersectionObserver gives the same "don't fetch below-fold sections" benefit
 * while the content stays IN FLOW, so the container is sized by its content and
 * the surrounding spacing is whatever the CSS says it is. It also drops the
 * dependency on requestAnimationFrame, which the HOC used and which is paused
 * in background tabs.
 *
 * @param {Object} ref React ref for the element to observe
 * @param {Object} [options]
 * @param {string} [options.rootMargin='400px'] how far ahead of the viewport to trigger
 * @returns {boolean} true once the element has come near the viewport
 */
const useNearViewport = (ref, options) => {
  const rootMargin = options?.rootMargin || '400px';
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) {
      return;
    }
    // No IntersectionObserver (older browsers, jsdom): load immediately rather
    // than leave the section permanently empty.
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setShouldLoad(true);
      return;
    }
    const el = ref.current;
    if (!el) {
      return;
    }
    const observer = new window.IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoad, ref, rootMargin]);

  return shouldLoad;
};

export default useNearViewport;
